/**
 * Shared display helpers for audit event types and categories.
 *
 * Extracted from log-filters.tag (METALARK-773) so the event-type popup
 * (event-type-select.tag) and the active-filter badges in log-filters.tag
 * render identical labels/emoji without duplicating the maps.
 *
 * Labels prefer locale keys and fall back to the auto-generated
 * formatEventType / category name:
 *   - per event:    admin_auditlogs_evt_<lowercased,no-underscores>
 *   - per category: admin_auditlogs_catgroup_<key,no-underscores>
 */

import { formatEventType, EVENT_ICONS } from './auditEventCategories.js';

// One emoji per category, applied to category rows and as the fallback for
// events without a specific icon mapping. New categories without a mapping
// fall back to "•".
export const CATEGORY_EMOJI = {
    AUTHENTICATION: '🔑',
    ACCESS_CONTROL: '🛡️',
    USER_LIFECYCLE: '👥',
    MFA: '🔒',
    ROLE_MANAGEMENT: '👤',
    IDENTITY_PROVIDER: '🔐',
    DATA_PRIVACY: '💾',
    CONFIGURATION: '⚙️',
    CAMPAIGN: '📣',
    WEBHOOKS: '🔗',
    COMPLIANCE: '📋',
    IMPERSONATION: '👔',
    PASSKEY: '👆',
    WORKSPACE_ACCESS: '🚪',
};

// Recovers the per-event emoji variety the hand-curated list had without
// re-introducing per-event maintenance: the auto-generated EVENT_ICONS map
// already pairs each event with a semantic CSS icon class (icon-key,
// icon-lock, icon-mail, …), and there are only ~25 distinct classes across
// the whole event set. Mapping classes once gives every event a distinctive
// emoji, and a new event added to AuditEventDescriptions.ts inherits one as
// soon as its `icon` field is set. Class strings in EVENT_ICONS look like
// "icon-key text-success" — split off the color modifier and key on the
// icon class alone.
export const ICON_CLASS_EMOJI = {
    'icon-blocked': '🚫',
    'icon-cancel-circle': '❌',
    'icon-checkbox-checked': '✅',
    'icon-checkbox-unchecked': '❌',
    'icon-clock': '⏰',
    'icon-database': '💾',
    'icon-download': '📥',
    'icon-enter': '↪️',
    'icon-file-text': '📄',
    'icon-key': '🔑',
    'icon-link': '🔗',
    'icon-lock': '🔒',
    'icon-mail': '📧',
    'icon-metalark-fingerprint': '👆',
    'icon-metalark-log-out': '🚪',
    'icon-metalark-logout': '↩️',
    'icon-metalark-settings': '🔧',
    'icon-notification': '🔔',
    'icon-refresh': '🔄',
    'icon-settings': '⚙️',
    'icon-shield': '🛡️',
    'icon-unlocked': '🔓',
    'icon-user-check': '✏️',
    'icon-user-minus': '➖',
    'icon-user-plus': '➕',
    'icon-user-tie': '👔',
    'icon-vpn_key': '🔐',
    'icon-warning': '⚠️',
};

/**
 * Localized label for a category, falling back to the generated name.
 * @param {object|null} locale - the injected locale service (may be null pre-mount)
 */
export function categoryGroupLabel(locale, categoryKey, category) {
    const localeKey = `admin_auditlogs_catgroup_${categoryKey.toLowerCase().replace(/_/g, '')}`;
    const translated = locale?.t(localeKey);
    if (translated && translated !== localeKey) return translated;
    return category?.name || categoryKey;
}

/**
 * Localized label for a single event type, falling back to formatEventType.
 */
export function eventLabel(locale, eventType) {
    const localeKey = `admin_auditlogs_evt_${eventType.toLowerCase().replace(/_/g, '')}`;
    const translated = locale?.t(localeKey);
    if (translated && translated !== localeKey) return translated;
    return formatEventType(eventType);
}

/**
 * Pull the icon class out of EVENT_ICONS["EVENT_TYPE"] (e.g.
 * "icon-key text-success" → "icon-key") and map it to an emoji. Returns ''
 * when there's no specific mapping so the caller can fall back to the
 * category emoji.
 */
export function eventEmoji(eventType) {
    const iconClasses = EVENT_ICONS[eventType];
    if (!iconClasses) return '';
    const iconClass = iconClasses.split(/\s+/)[0];
    return ICON_CLASS_EMOJI[iconClass] || '';
}
